import { RESPONSIVE_WIDTHS } from './image-widths.mjs';

/**
 * Responsive image utility for CDN-delivered assets.
 *
 * Provides helpers to build srcSet strings, detect CDN URLs, generate
 * sizes attributes, and manipulate file extensions — all designed to
 * work with the Trenchmate CDN (cdn.trenchmate.techstream.co.nz) which
 * serves pre-scaled variants (e.g. trench-shield-400w.webp).
 *
 * Widths are declared once in image-widths.mjs.
 */

/**
 * Build a srcSet string for responsive image variants on CDN.
 *
 * Given a base path like "images/20130508_092219" and extension "webp",
 * generates:
 *   "...-400w.webp 400w, ...-800w.webp 800w, ...-1200w.webp 1200w, ...-1920w.webp 1920w"
 *
 * @param {string} basePath — path without extension, e.g. "product/trench-shield/trench-shield"
 * @param {string} ext — file extension without dot, e.g. "webp" or "jpg"
 * @param {number[]} [widths] — responsive widths (default from RESPONSIVE_WIDTHS)
 * @returns {string} srcSet string
 */
export function buildResponsiveSrcSet(basePath, ext, widths) {
    if (!basePath || !ext) return '';
    if (typeof basePath !== 'string' || typeof ext !== 'string') return '';

    const sizes =
        Array.isArray(widths) && widths.length > 0 ? widths : RESPONSIVE_WIDTHS;

    return sizes
        .filter((w) => Number.isFinite(w) && w > 0)
        .sort((a, b) => a - b)
        .map((w) => `${basePath}-${w}w.${ext.replace(/^\./, '')} ${w}w`)
        .join(', ');
}

/**
 * Check if a URL points to the Trenchmate CDN.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isCdnUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return /cdn\.trenchmate\.techstream\.co\.nz/i.test(url);
}

/**
 * Layout presets for the `sizes` attribute.
 */
const SIZES_PRESETS = {
    hero: '(max-width: 768px) 100vw, 100vw',
    card: '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw',
    gallery: '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
    cover: '(max-width: 768px) 100vw, 50vw',
    'full-width': '100vw',
};

/**
 * Get a sizes attribute string based on a layout preset.
 *
 * @param {string} layout — one of: 'hero', 'card', 'gallery', 'cover', 'full-width'
 * @returns {string} sizes attribute value
 */
export function getSizesForLayout(layout) {
    if (!layout || typeof layout !== 'string')
        return SIZES_PRESETS['full-width'];
    return SIZES_PRESETS[layout] || SIZES_PRESETS['full-width'];
}

/**
 * Strip extension from a file path, removing the last .ext suffix.
 *
 * @param {string} path
 * @returns {string}
 */
export function stripExtension(path) {
    if (!path || typeof path !== 'string') return '';
    return path.replace(/\.[^/.]+$/, '');
}

/**
 * Get the file extension without dot from a path.
 *
 * @param {string} path
 * @returns {string|null}
 */
export function getExtension(path) {
    if (!path || typeof path !== 'string') return null;
    const match = path.match(/\.([^./]+)$/);
    return match ? match[1] : null;
}

/**
 * Determine whether an image is an SVG based on its path or URL.
 *
 * @param {string} src
 * @returns {boolean}
 */
export function isSvg(src) {
    if (!src || typeof src !== 'string') return false;
    return /\.svg$/i.test(src.split('?')[0].split('#')[0]);
}
